// パフォーマンス最適化ユーティリティ

/**
 * 画像の遅延読み込み用のインターセクションオブザーバー
 */
export const createLazyImageObserver = (callback: (entry: IntersectionObserverEntry) => void) => {
  const options: IntersectionObserverInit = {
    root: null,
    rootMargin: '100px',
    threshold: 0.1,
  };

  return new IntersectionObserver((entries) => {
    entries.forEach(callback);
  }, options);
};

/**
 * デバウンス関数 - イベントハンドラーの最適化
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * スロットル関数 - スクロールイベントなどの最適化
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * モーション設定の検出
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * デバイス検出
 */
export const getDeviceInfo = () => {
  const isMobile = window.innerWidth < 768;
  const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
  const isDesktop = window.innerWidth >= 1024;
  const isTouchDevice = 'ontouchstart' in window;
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
  };
};

/**
 * 画像プリロード
 */
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * 複数画像のプリロード
 */
export const preloadImages = (sources: string[]): Promise<void[]> => {
  return Promise.all(sources.map(preloadImage));
};

/**
 * WebP サポート検出
 */
export const supportsWebP = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

/**
 * リソースヒント
 */
export const addResourceHint = (href: string, rel: 'preload' | 'prefetch' | 'dns-prefetch' | 'preconnect', as?: string) => {
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (as && (rel === 'preload' || rel === 'prefetch')) {
    link.as = as;
  }
  document.head.appendChild(link);
};

/**
 * Critical リソースのプリロード
 */
export const preloadCriticalResources = () => {
  const criticalImages = [
    '/images/about/5308A932-C229-4580-BE04-0F6ED13F962A.jpg',
    '/images/iris/irisi_image.jpg',
    '/images/hero/1.jpg',
  ];

  const criticalVideos = [
    '/videos/fastiny_entertainment_bar_promo.mov',
  ];

  // 画像のプリロード
  criticalImages.forEach(src => {
    addResourceHint(src, 'preload', 'image');
  });

  // 動画のプリロード
  criticalVideos.forEach(src => {
    addResourceHint(src, 'preload', 'video');
  });

  // フォントのプリロード
  addResourceHint('https://fonts.googleapis.com', 'preconnect');
  addResourceHint('https://fonts.gstatic.com', 'preconnect');
};

/**
 * Core Web Vitals の測定
 */
export const measureWebVitals = () => {
  // First Contentful Paint
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      console.log(`${entry.name}: ${entry.startTime}ms`);
    }
  });

  observer.observe({ entryTypes: ['paint', 'largest-contentful-paint', 'first-input'] });
};

/**
 * メモリ使用量の監視
 */
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    console.log('Memory usage:', {
      used: `${Math.round(memory.usedJSHeapSize / 1048576)} MB`,
      total: `${Math.round(memory.totalJSHeapSize / 1048576)} MB`,
      limit: `${Math.round(memory.jsHeapSizeLimit / 1048576)} MB`,
    });
  }
};

/**
 * レスポンシブ画像のソース生成
 */
export const generateResponsiveImageSources = (basePath: string, filename: string) => {
  const name = filename.split('.')[0];
  const ext = filename.split('.').pop();
  
  return {
    webp: {
      mobile: `${basePath}/${name}-mobile.webp`,
      tablet: `${basePath}/${name}-tablet.webp`,
      desktop: `${basePath}/${name}-desktop.webp`,
    },
    fallback: {
      mobile: `${basePath}/${name}-mobile.${ext}`,
      tablet: `${basePath}/${name}-tablet.${ext}`,
      desktop: `${basePath}/${name}-desktop.${ext}`,
      original: `${basePath}/${filename}`,
    }
  };
};

/**
 * ビューポート最適化
 */
export const optimizeViewport = () => {
  // ビューポートメタタグの動的更新
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
  }
};

/**
 * Service Worker 登録
 */
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
}; 